import {ApolloClient} from "@apollo/client";
import {action, makeObservable, observable} from "mobx";
import cuid from "cuid";

import {DataConstants} from "../models/constants";
import {QUERY_PREDICATE_KIND_COMPARTMENT, QUERY_PREDICATE_KIND_SPHERE} from "./queryPredicateKind";
import {SEARCH_NEURONS_QUERY, SearchContext, SearchNeuronsQueryResponse, SearchNeuronsQueryVariables} from "../graphql/search";
import {PredicateComposition, FilterContents, IPositionInput, RestoreTally} from "./filterContents";
import {UserPreferences} from "../util/userPreferences";
import {QueryResponseState, QueryResponseViewModel} from "./queryResponseViewModel";
import {UIQueryPredicate, UIQueryPredicateState} from "./uiQueryPredicate";

export type UIQueryState = {
    predicates: UIQueryPredicateState[];
    limitCollections: boolean;
    collectionIds: string[];
    atlasKindIds?: string[];
}

export class UIQuery {
    public resetCount: number = 0;

    public predicates: UIQueryPredicate[] = [];

    public limitCollections: boolean = false;

    public collectionIds: string[] = [];

    public atlasKindIds: string[] = [];

    private _constants: DataConstants = null;

    public constructor() {
        makeObservable(this, {
            predicates: observable,
            limitCollections: observable,
            collectionIds: observable,
            atlasKindIds: observable,
            resetCount: observable,
            reset: action,
            initialize: action,
            addPredicate: action,
            removePredicate: action,
            replacePredicate: action,
            execute: action,
            deserialize: action,
            deserializePredicates: action
        });

        this.reset();
    }

    public reset() {
        this.resetCount++;

        this.predicates = [Object.assign(new UIQueryPredicate(), DEFAULT_QUERY_FILTER(), {id: cuid()})];
    }

    public initialize(constants: DataConstants) {
        if (constants === this._constants) {
            return;
        }

        const isFirst = this._constants == null;

        this._constants = constants;

        if (isFirst) {
            this.atlasKindIds = this.defaultAtlasKindIds();
        }
    }

    // Returns the number of saved atlas structure ids that could not be resolved.
    public deserializePredicates(serializedPredicates: any[]): number {
        const tally: RestoreTally = {unresolvedStructures: 0};

        if (serializedPredicates && serializedPredicates.length > 0 && this._constants) {
            this.predicates = serializedPredicates.map(f => {
                return UIQueryPredicate.deserialize(f, this._constants, tally);
            });
        }

        return tally.unresolvedStructures;
    }

    public addPredicate(uiModifiers: any = {}, predicateModifiers: any = {}) {
        const predicate = Object.assign(new UIQueryPredicate(), DEFAULT_QUERY_FILTER(), {
            id: cuid(),
            index: this.predicates.length,
            filter: Object.assign(new FilterContents(this.predicates.length === 0), predicateModifiers)
        }, uiModifiers);

        this.predicates.push(predicate);
    }

    public removePredicate(id: string) {
        this.predicates = this.predicates.filter(q => q.id !== id).map((q, idx) => {
            q.index = idx;
            return q;
        });
    }

    public replacePredicate(filter: UIQueryPredicate) {
        if (this.predicates.length > filter.index) {
            this.predicates[filter.index] = filter;
        }
    }

    public createCustomRegionPredicate(position: IPositionInput, replace: boolean) {
        if (replace) {
            const filter = this.predicates[this.predicates.length - 1];
            filter.brainAreaFilterType = QUERY_PREDICATE_KIND_SPHERE;
            filter.filter.arbCenter = {
                x: position.x.toFixed(1),
                y: position.y.toFixed(1),
                z: position.z.toFixed(1)
            };
            this.replacePredicate(filter);
        } else {
            this.addPredicate({
                brainAreaFilterType: QUERY_PREDICATE_KIND_SPHERE
            }, {
                composition: PredicateComposition.and,
                arbCenter: {
                    x: position.x.toFixed(1),
                    y: position.y.toFixed(1),
                    z: position.z.toFixed(1)
                }
            });
        }
    };

    public async execute(queryResponseViewModel: QueryResponseViewModel, client: ApolloClient<object>, responseState: QueryResponseState = null) {
        queryResponseViewModel.initiate(cuid());

        try {
            UserPreferences.Instance.LastQuery = this.predicates.map(f => f.serialize());

            const context: SearchContext = {
                nonce: queryResponseViewModel.queryNonce,
                collectionIds: this.limitCollections ? this.collectionIds : [],
                atlasKindIds: this.atlasKindIds,
                predicates: this.predicates.map(f => f.asSearchPredicate())
            };

            const {data, error} = await client.query<SearchNeuronsQueryResponse, SearchNeuronsQueryVariables>({
                query: SEARCH_NEURONS_QUERY,
                variables: {context}
            });

            if (error) {
                queryResponseViewModel.errored(error);
                console.error(error);
                return;
            }

            queryResponseViewModel.update(data.searchNeurons.queryTime, data.searchNeurons.neurons, data.searchNeurons.totalCount);
            this._constants.neuronCount = data.searchNeurons.totalCount;

            if (responseState) {
                queryResponseViewModel.deserialize(responseState);
            }

        } catch (err) {
            queryResponseViewModel.errored(err);
            console.error(err);
        }
    }

    public serialize(): UIQueryState {
        return {
            predicates: this.predicates.map(p => p.serialize()),
            limitCollections: this.limitCollections,
            collectionIds: this.collectionIds,
            atlasKindIds: this.atlasKindIds
        }
    }

    // Returns the number of saved atlas structure ids that could not be resolved.
    public deserialize(state: UIQueryState): number {
        const tally: RestoreTally = {unresolvedStructures: 0};

        this.predicates = state.predicates.map(p => UIQueryPredicate.deserialize(p, this._constants, tally));
        this.limitCollections = state.limitCollections;
        this.collectionIds = state.collectionIds;
        // Absent in state saved before atlas kinds; an explicit empty list means no restriction and is kept.
        this.atlasKindIds = Array.isArray(state.atlasKindIds) ? state.atlasKindIds : this.defaultAtlasKindIds();

        return tally.unresolvedStructures;
    }

    private defaultAtlasKindIds(): string[] {
        const kind = this._constants?.DefaultAtlasKind;

        return kind ? [kind.id] : [];
    }
}

export const DEFAULT_QUERY_FILTER = (): UIQueryPredicate => Object.assign(new UIQueryPredicate(), {
    id: "",
    index: 0,
    brainAreaFilterType: QUERY_PREDICATE_KIND_COMPARTMENT,
    filter: new FilterContents(true)
});
